import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Send, Clock, MessageCircle, HelpCircle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";

const TOPICS = [
  "General question",
  "Billing / refund",
  "Bug report",
  "Feature request",
  "Partnership",
];

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState(TOPICS[0]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 10) {
      toast.error("Please write a message of at least 10 characters.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", {
        name: name.trim(),
        email: email.trim(),
        topic,
        message: message.trim(),
      });
      setSent(true);
      toast.success("Message received. We'll reply within 1 business day.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail[0]?.msg || "Please check your inputs."
        : detail || "Could not send. Try emailing us directly.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <main
        data-testid="contact-page"
        className="mx-auto max-w-5xl px-6 pb-12 pt-12 lg:px-10 lg:pt-20"
      >
        <div className="mb-10 sm:mb-14">
          <p className="mb-3 text-xs uppercase tracking-[0.25em] text-neutral-500">
            — Get in touch
          </p>
          <h1 className="font-serif text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
            Contact
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-600 sm:text-lg">
            Questions, feedback, or support — we usually reply within{" "}
            <span className="font-medium text-neutral-900">1 business day</span>.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-5 lg:gap-12">
          {/* Info column */}
          <aside className="lg:col-span-2">
            <div className="space-y-6">
              <div
                className="border border-neutral-200 bg-white p-6 shadow-soft"
                data-testid="contact-email-card"
              >
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500">
                  <Mail className="h-3.5 w-3.5 text-coral" strokeWidth={1.5} />
                  Email
                </div>
                <a
                  href="mailto:support@postshifted.com"
                  data-testid="contact-email-link"
                  className="mt-2 block font-serif text-2xl font-semibold text-neutral-900 hover:text-coral"
                >
                  support@postshifted.com
                </a>
                <p className="mt-2 text-xs text-neutral-500">
                  Fastest for account and billing issues.
                </p>
              </div>

              <div className="border border-neutral-200 bg-white p-6 shadow-soft">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500">
                  <Clock className="h-3.5 w-3.5 text-coral" strokeWidth={1.5} />
                  Response time
                </div>
                <p className="mt-2 font-serif text-lg text-neutral-900">
                  Within 1 business day
                </p>
                <p className="mt-1 text-xs text-neutral-500">
                  Monday - Friday. Weekends we&apos;re a little slower.
                </p>
              </div>

              <div className="border border-neutral-200 bg-white p-6 shadow-soft">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500">
                  <HelpCircle className="h-3.5 w-3.5 text-coral" strokeWidth={1.5} />
                  Before you write
                </div>
                <ul className="mt-3 space-y-2 text-sm text-neutral-700">
                  <li>
                    Billing / refund questions:{" "}
                    <Link
                      to="/refund"
                      className="text-coral underline underline-offset-4 hover:text-coral-hover"
                    >
                      Refund Policy
                    </Link>
                  </li>
                  <li>
                    Privacy / data:{" "}
                    <Link
                      to="/privacy"
                      className="text-coral underline underline-offset-4 hover:text-coral-hover"
                    >
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    Terms of the service:{" "}
                    <Link
                      to="/terms"
                      className="text-coral underline underline-offset-4 hover:text-coral-hover"
                    >
                      Terms of Service
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </aside>

          {/* Form column */}
          <section className="lg:col-span-3">
            {sent ? (
              <div
                data-testid="contact-sent-panel"
                className="border border-neutral-200 bg-white p-8 shadow-soft"
              >
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-coral-subtle">
                  <MessageCircle className="h-5 w-5 text-coral" strokeWidth={1.5} />
                </div>
                <h2 className="font-serif text-3xl font-bold tracking-tight">
                  Message received.
                </h2>
                <p className="mt-3 max-w-md text-sm text-neutral-600">
                  Thanks for reaching out. We&apos;ll reply to{" "}
                  <span className="font-medium text-neutral-900">{email}</span> within
                  one business day. If it&apos;s urgent, email us at{" "}
                  <a
                    href="mailto:support@postshifted.com"
                    className="text-coral underline underline-offset-4"
                  >
                    support@postshifted.com
                  </a>{" "}
                  as well.
                </p>
                <Button
                  onClick={() => {
                    setSent(false);
                    setName("");
                    setEmail("");
                    setMessage("");
                    setTopic(TOPICS[0]);
                  }}
                  variant="outline"
                  data-testid="contact-send-another"
                  className="mt-6 h-11 rounded-[3px] border-neutral-300"
                >
                  Send another message
                </Button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="border border-neutral-200 bg-white p-6 shadow-soft sm:p-8"
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <Label
                      htmlFor="contact-name"
                      className="text-xs uppercase tracking-widest text-neutral-500"
                    >
                      Name
                    </Label>
                    <Input
                      id="contact-name"
                      required
                      minLength={1}
                      maxLength={100}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      data-testid="contact-name-input"
                      placeholder="Your name"
                      className="mt-2 h-11 rounded-[3px] border-neutral-300 focus-visible:border-coral focus-visible:ring-1 focus-visible:ring-coral"
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="contact-email"
                      className="text-xs uppercase tracking-widest text-neutral-500"
                    >
                      Email
                    </Label>
                    <Input
                      id="contact-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="contact-email-input"
                      placeholder="you@example.com"
                      className="mt-2 h-11 rounded-[3px] border-neutral-300 focus-visible:border-coral focus-visible:ring-1 focus-visible:ring-coral"
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <Label
                    htmlFor="contact-topic"
                    className="text-xs uppercase tracking-widest text-neutral-500"
                  >
                    Topic
                  </Label>
                  <select
                    id="contact-topic"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    data-testid="contact-topic-select"
                    className="mt-2 h-11 w-full cursor-pointer rounded-[3px] border border-neutral-300 bg-white px-3 text-sm text-neutral-900 focus:border-coral focus:outline-none focus:ring-1 focus:ring-coral"
                  >
                    {TOPICS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-5">
                  <Label
                    htmlFor="contact-message"
                    className="text-xs uppercase tracking-widest text-neutral-500"
                  >
                    Message
                  </Label>
                  <Textarea
                    id="contact-message"
                    required
                    minLength={10}
                    maxLength={4000}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    data-testid="contact-message-input"
                    placeholder="How can we help?"
                    className="mt-2 min-h-[160px] resize-y rounded-[3px] border-neutral-300 focus-visible:border-coral focus-visible:ring-1 focus-visible:ring-coral"
                  />
                  <div className="mt-1 flex justify-between text-[11px] text-neutral-400">
                    <span>Min 10 characters. Include as much detail as you can.</span>
                    <span className="font-mono">{message.length} / 4,000</span>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="mt-6 h-12 w-full rounded-[3px] bg-coral text-white transition-all duration-300 hover:bg-coral-hover hover:shadow-coral disabled:opacity-50 sm:w-auto sm:px-8"
                >
                  <Send className="mr-2 h-4 w-4" strokeWidth={1.5} />
                  {loading ? "Sending…" : "Send message"}
                </Button>

                <p className="mt-4 text-[11px] leading-relaxed text-neutral-500">
                  By sending, you agree to our{" "}
                  <Link to="/terms" className="underline underline-offset-4 hover:text-coral">
                    Terms
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="underline underline-offset-4 hover:text-coral">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </form>
            )}
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
